import { PatientAnswerDataTypeFragment } from './getIntakeNoteComprehensivePatientData.generated'

export function mapPatientAnswersToForm(patientAnswers?: PatientAnswerDataTypeFragment | null) {
  if (!patientAnswers) return {}

  return {
    BIOPYCHOSOCIAL_EVALUATION: {
      CURRENT_LIVING_SITUATION: getStringValue(patientAnswers.currentLivingSituation),
      ALLERGIES: getUnionLiteral(patientAnswers.hasAllergies),
      ALLERGIES_DETAILS: getStringValue(patientAnswers.listAllergies),
      MEDICAL_CONDITIONS: getUnionLiteral(
        getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Other conditions',
        })
      ),
      MEDICAL_CONDITIONS_DETAILS: getStringValue(patientAnswers.otherConditions),
      MEDICATIONS: getUnionLiteral(patientAnswers.takesMedication),
      MEDICATIONS_DETAILS:
        patientAnswers.medications && patientAnswers.medications.length > 0
          ? patientAnswers.medications.map((medication) => ({
              NAME: getStringValue(medication?.name),
              DOSAGE: getStringValue(medication?.dosage),
              FREQUENCY: getStringValue(medication?.frequency),
            }))
          : undefined,
      PRIMARY_CARE_DOCTOR:
        patientAnswers.doctorName && patientAnswers.doctorPhoneNumber
          ? {
              NAME: getStringValue(patientAnswers.doctorName),
              PHONE: getStringValue(patientAnswers.doctorPhoneNumber),
            }
          : undefined,
      SUBSTANCE_ABUSE: {
        TOBACCO: getSubstanceAbuseValue(patientAnswers.tobaccoCigarettesChewVape),
        ALCOHOL: getSubstanceAbuseValue(patientAnswers.alcohol),
        MARIJUANA: getSubstanceAbuseValue(patientAnswers.marijuana),
        NON_PRESCRIBED_DRUGS: getSubstanceAbuseValue(patientAnswers.nonprescriptionDrugs),
        OTHER_RECREATIONAL_DRUGS: getSubstanceAbuseValue(patientAnswers.otherRecreationalSubstances),
        REPORTS_INTEREST_TO_REDUCE_USE: getBooleanOfOption({
          input: patientAnswers.substanceUseSupport,
          optionValue: 'I’m interested in exploring options to reduce my use.',
        }),
        REPORTS_RECEIVING_SUPPORT: getBooleanOfOption({
          input: patientAnswers.substanceUseSupport,
          optionValue: 'I’m currently receiving support for substance use.',
        }),
        SUPPORT_DETAILS: getStringValue(patientAnswers.supportDetails),
      },
      FAMILY_HISTORY: getUnionLiteral(patientAnswers.mentalIllnessHistoryInFamily),
      FAMILY_HISTORY_ISSUES: {
        TRAUMA_OR_PTSD: getBooleanOfOption({
          input: patientAnswers.mentalIllnessDiagnosisFamily,
          optionValue: 'Trauma or PTSD',
        }),
        SUBSTANCE_ABUSE: getBooleanOfOption({
          input: patientAnswers.mentalIllnessDiagnosisFamily,
          optionValue: 'Substance abuse',
        }),
        ANXIETY: getBooleanOfOption({
          input: patientAnswers.mentalIllnessDiagnosisFamily,
          optionValue: 'Anxiety',
        }),
        SCHIZOPHRENIA: getBooleanOfOption({
          input: patientAnswers.mentalIllnessDiagnosisFamily,
          optionValue: 'Schizophrenia',
        }),
        SUICIDE: getBooleanOfOption({
          input: patientAnswers.mentalIllnessDiagnosisFamily,
          optionValue: 'Suicide',
        }),
        DEPRESSION: getBooleanOfOption({
          input: patientAnswers.mentalIllnessDiagnosisFamily,
          optionValue: 'Depression',
        }),
        BIPOLAR_DISORDER: getBooleanOfOption({
          input: patientAnswers.mentalIllnessDiagnosisFamily,
          optionValue: 'Bipolar disorder',
        }),
        ADHD: getBooleanOfOption({
          input: patientAnswers.mentalIllnessDiagnosisFamily,
          optionValue: 'ADHD',
        }),
        INCARCERATION: getBooleanOfOption({
          input: patientAnswers.mentalIllnessDiagnosisFamily,
          optionValue: 'Incarceration',
        }),
      },
      MEDICAL_HISTORY: {
        ARTHRITIS: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Arthritis',
        }),
        ASTHMA: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Asthma',
        }),
        AUTOIMMUNE_DISORDER: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Autoimmune disorder',
        }),
        BACK_PROBLEMS: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Back problems',
        }),
        CANCER_CURRENT_OR_PAST: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Cancer (current or past)',
        }),
        CHRONIC_PAIN: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Chronic pain',
        }),
        COPD_EMPHYSEMA: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'COPD/emphysema',
        }),
        DIABETES: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Diabetes',
        }),
        HEART_DISEASE: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Heart disease',
        }),
        HIGH_BLOOD_PRESSURE: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'High blood pressure',
        }),
        HIGH_CHOLESTEROL: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'High cholesterol',
        }),
        HORMONAL_IMBALANCE: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Hormonal imbalance',
        }),
        IBS_CROHNS_COLITIS: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'IBS / Crohn’s / Colitis',
        }),
        LIVER_DISEASE: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Liver disease',
        }),
        MIGRAINES: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Migraines',
        }),
        SEIZURES: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Seizures',
        }),
        SLEEP_APNEA: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Sleep apnea',
        }),
        STROKE: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Stroke',
        }),
        THYROID_DISORDER: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Thyroid disorder',
        }),
        TRAUMATIC_BRAIN_INJURY: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Traumatic brain injury',
        }),
        ULCERS: getBooleanOfOption({
          input: patientAnswers.medicalHistory,
          optionValue: 'Ulcers',
        }),
      },
    },
    REASON_FOR_SEEKING_TREATMENT: {
      NARRATIVE: getStringValue(patientAnswers.mainReasonForSeekingSupport),
      FUNCTIONAL_IMPAIRMENTS: {
        DIFFICULTY_MEETING_EXPECTATIONS: getBooleanOfOption({
          input: patientAnswers.functionalImpairments,
          optionValue:
            'Difficulty meeting expectations at work or school (absenteeism, frequent mistakes, inability to focus on tasks).',
        }),
        STRUGGLE_WITH_RELATIONSHIPS: getBooleanOfOption({
          input: patientAnswers.functionalImpairments,
          optionValue:
            'Struggles with relationships (withdrawing from friends or family, conflict with peers, difficulty trusting others).',
        }),
        NEGLECT_OF_PERSONAL_NEEDS: getBooleanOfOption({
          input: patientAnswers.functionalImpairments,
          optionValue:
            'Neglect of personal grooming, nutrition or medication routines (poor bathing habits, skipping meals, missing prescribed doses of medication).',
        }),
        DIFFICULTY_IN_SLEEP: getBooleanOfOption({
          input: patientAnswers.functionalImpairments,
          optionValue:
            'Insomnia, hypersomnia or irregular sleep patterns (difficulty falling or staying asleep, excessive daytime sleepiness).',
        }),
        CHANGE_IN_APPETITE_OR_WEIGHT: getBooleanOfOption({
          input: patientAnswers.functionalImpairments,
          optionValue:
            'Marked increase or decrease in appetite or body weight (binge-eating episodes, loss of appetite, significant unintentional weight loss/gain).',
        }),
        IMPAIRED_COGNITIVE_FUNCTIONING: getBooleanOfOption({
          input: patientAnswers.functionalImpairments,
          optionValue:
            'Impaired cognitive functioning (trouble staying on task, forgetfulness, difficulty processing information).',
        }),
        VOLATILE_MOOD_RESPONSES: getBooleanOfOption({
          input: patientAnswers.functionalImpairments,
          optionValue:
            'Volatile or blunted mood responses (frequent irritability or anger outbursts, emotional numbness, difficulty calming down).',
        }),
        DIFFICULTY_MANAGING_TASKS: getBooleanOfOption({
          input: patientAnswers.functionalImpairments,
          optionValue:
            'Challenges managing household or other tasks (forgetting to pay bills, inability to shop or prepare meals, neglecting chores).',
        }),
        LOSS_OF_INTEREST: getBooleanOfOption({
          input: patientAnswers.functionalImpairments,
          optionValue: 'Loss of interest or pleasure in hobbies.',
        }),
      },
    },
    MENTAL_HEALTH_TREATMENT_HISTORY: {
      SEEN_MENTAL_HEALTH_PROFESSIONAL_IN_PAST: Boolean(patientAnswers.hasSeenAMentalHealthProfessional),
      CARE_DETAILS: getStringValue(patientAnswers.detailsOfExperienceWithMentalHealthProfessional),
      HOSPITALIZED_FOR_MENTAL_HEALTH: Boolean(patientAnswers.hasBeenHospitalized),
      HOSPITALIZATION_DETAILS: getStringValue(patientAnswers.detailsOfHospitalization),
    },
  }
}

function getSubstanceAbuseValue(input?: string | null) {
  switch (input) {
    case 'None':
      return 'NONE'
    case 'Using':
      return 'USING'
    case 'Have used':
      return 'HAVE_USED'
    default:
      return 'NONE'
  }
}

function getStringValue(input?: string | null) {
  if (!input) return ''

  return input.trim()
}

function getBooleanOfOption({ input, optionValue }: { input?: string[] | null; optionValue: string }) {
  return Boolean(input?.includes(optionValue))
}

function getUnionLiteral(input?: boolean | null) {
  return input ? 'reports_yes' : 'reports_no'
}
